import { useEffect, useRef, useState } from 'react'
import { useApp } from '@/core/state/appStore'
import { FormRenderer } from '@/core/components/form/FormRenderer'
import { requiredValueEmpty } from '@/core/components/form/formModel'
import type { FormDefinition, FormFieldDefinition, ShellWerte } from '@/core/templates/types'
import { Button, Icon, ModalHead, Pill, ScrollBar, toastError, toastSuccess } from 'admination-react'
import type { TeilnehmendePerson } from '../../api/zuweisungsliste'
import { PERSON_FELDER, teilnehmendePersonAnlegen } from './anlegen'
import { letzterVergabeZeitraum } from './letzterZeitraum'

export const PERSON_FORM = {
  title: 'Teilnehmende Person anlegen',
  sections: {
    'Teilnehmende Person': {
      fields: [
        { id: 'kundennummer', label: 'Kundennummer', control: 'text', required: true, validateRequired: true },
        { id: 'anrede', label: 'Anrede', control: 'choice', options: ['Herr', 'Frau'], required: true, validateRequired: true },
        { id: 'vorname', label: 'Vorname', control: 'text', required: true, validateRequired: true },
        { id: 'nachname', label: 'Nachname', control: 'text', required: true, validateRequired: true },
      ],
    },
    Zuweisung: {
      fields: [
        { id: 'organisationszeichen', label: 'Organisationszeichen', control: 'text', required: true, validateRequired: true },
        { id: 'beginn', label: 'Beginn', control: 'date', required: true, validateRequired: true },
        { id: 'ende', label: 'Ende', control: 'date', required: true, validateRequired: true },
      ],
    },
  },
} satisfies FormDefinition

function personAus(values: ShellWerte): TeilnehmendePerson {
  const text = (id: (typeof PERSON_FELDER)[number]) => {
    const v = values[id]
    return typeof v === 'string' ? v.trim() : ''
  }
  return {
    kundennummer: text('kundennummer'),
    anrede: text('anrede'),
    vorname: text('vorname'),
    nachname: text('nachname'),
    organisationszeichen: text('organisationszeichen'),
    beginn: text('beginn'),
    ende: text('ende'),
  }
}

function offenText(n: number): string {
  return n === 1 ? "Noch 1 Pflichtfeld offen" : `Noch ${n} Pflichtfelder offen`
}

export function TeilnehmendePersonAnlegen() {
  const { dispatch, reloadDashboard } = useApp()
  const scrollerRef = useRef<HTMLElement>(null)
  const [values, setValues] = useState<ShellWerte>({})
  const [serverErrors, setServerErrors] = useState<Map<string, string> | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [versucht, setVersucht] = useState(false)
  const busyRef = useRef(false)
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && busyRef.current) e.stopPropagation()
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [])

  useEffect(() => {
    scrollerRef.current?.querySelector<HTMLElement>('textarea, input')?.focus({ preventScroll: true })
  }, [])

  useEffect(() => {
    letzterVergabeZeitraum().then(
      (zeitraum) => {
        if (!zeitraum || !alive.current) return
        setValues((prev) => ({
          ...prev,
          beginn: prev.beginn ?? zeitraum.beginn,
          ende: prev.ende ?? zeitraum.ende,
        }))
      },
      () => {
        if (alive.current) toastError("Der letzte Maßnahmezeitraum konnte nicht geladen werden.")
      },
    )
  }, [])

  const close = () => dispatch({ type: 'CLOSE' })

  function handleChange(id: string, value: unknown) {
    setValues((prev) => ({ ...prev, [id]: value as ShellWerte[string] }))
    setServerErrors((prev) => {
      if (!prev?.has(id)) return prev
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }

  const felder = Object.values(PERSON_FORM.sections).flatMap<FormFieldDefinition>((section) => section.fields)
  const offen = felder.filter((feld) => feld.required && requiredValueEmpty(values[feld.id])).length
  const hinweis = offen > 0 ? offenText(offen) : null

  async function anlegen() {
    if (busyRef.current) return
    if (offen > 0) {
      setVersucht(true)
      return
    }
    busyRef.current = true
    setBusy(true)
    const person = personAus(values)
    try {
      const ergebnis = await teilnehmendePersonAnlegen(person)
      switch (ergebnis.kind) {
        case 'angelegt':
          toastSuccess(`${person.vorname} ${person.nachname} angelegt.`)
          close()
          reloadDashboard()
          return
        case 'uebersprungen':
          toastError(
            `Unter der Kundennummer ${ergebnis.kundennummer} ist bereits eine teilnehmende Person angelegt. Es wurde nichts geändert.`,
          )
          if (alive.current) {
            setServerErrors(new Map([['kundennummer', "Bereits als teilnehmende Person angelegt"]]))
          }
          return
        case 'abgelehnt':
          toastError([ergebnis.meldung, ...ergebnis.sonstige].join(' '))
          if (alive.current) setServerErrors(ergebnis.felder.size > 0 ? ergebnis.felder : undefined)
          return
        case 'unlesbar':
          reloadDashboard()
          toastError("Die Antwort des Servers war unvollständig. Bitte prüfen Sie das Board.")
          return
        case 'fehler':
          toastError(ergebnis.meldung)
          return
      }
    } finally {
      busyRef.current = false
      if (alive.current) setBusy(false)
    }
  }

  return (
    <>
      <ModalHead titleIcon="i-person"
        title={PERSON_FORM.title}
        actions={
          <>
            {hinweis && <span className="field-hint is-info">{hinweis}</span>}
            <Pill>
              <Button
                primaryAction
                loading={busy}
                disabled={busy}
                onClick={() => void anlegen()}
              >
                <Icon name="i-check" />
                Anlegen</Button>
            </Pill>
          </>
        }
        onClose={close}
        closeLabel="Schließen"
        closeDisabled={busy}
      />
      <main className="form" data-teilnehmende-person-anlegen data-scroll-host ref={scrollerRef}>
        <FormRenderer
            definition={PERSON_FORM}
            values={values}
            onChange={handleChange}
            attemptedAdvance={versucht}
            field={(field) => ({ errorMessage: serverErrors?.get(field.id) })}
          />
      </main>
      <ScrollBar scrollerRef={scrollerRef} />
    </>
  )
}
